import { useState } from 'react';
import type { CSSProperties, ReactNode } from 'react';
import { Trans, useTranslation } from 'react-i18next';

import { Button } from '@actual-app/components/button';
import { Text } from '@actual-app/components/text';
import { View } from '@actual-app/components/view';

import {
  CADA_MIN,
  frasesToque,
  horasDelDia,
  nombreRitmo,
  proximoToque,
  RITMOS,
  textoMinutos,
  TRAS_PENDIENTE,
} from '#components/mobile/avisos/avisos';
import type { AvisosGavi } from '#components/mobile/avisos/avisos';
import {
  mostrarAviso,
  pedirPermisoAvisos,
  permisoAvisos,
  useAvisos,
} from '#components/mobile/avisos/useAvisos';
import {
  Chip,
  Interruptor,
  Marca,
} from '#components/mobile/travesia/pasos/comun';
import { Boton } from '#components/mobile/ui/Boton';
import { color, radio, TACTIL, texto } from '#components/mobile/ui/tokens';
import { useTripulacion } from '#components/pareja/useTripulacion';

/**
 * Ajustes → «Notificaciones»: cada cuánto recuerda Gavi apuntar gastos y
 * revisar, el aviso de lo que quedó pendiente y cómo suenan. Todo es de
 * este dispositivo (pref global `avisosGavi`). Copia de la pantalla A de la
 * maqueta «Avisos de Gavi» (17-oct-2026).
 */
export function NotificacionesSettings() {
  const { t, i18n } = useTranslation();
  const { avisos: c, setAvisos } = useAvisos();
  const [permiso, setPermiso] = useState(permisoAvisos);
  const [prueba, setPrueba] = useState<'enviada' | 'fallo' | null>(null);
  // Pareja: el aviso solo tiene sentido en un presupuesto compartido.
  const compartido = useTripulacion().length > 1;

  const cambiar = (cambios: Partial<AvisosGavi>) =>
    setAvisos({ ...c, ...cambios });
  const cambiarPendiente = (cambios: Partial<AvisosGavi['pendiente']>) =>
    cambiar({ pendiente: { ...c.pendiente, ...cambios } });
  const cambiarSilencio = (cambios: Partial<AvisosGavi['silencio']>) =>
    cambiar({ silencio: { ...c.silencio, ...cambios } });

  async function activar(encendido: boolean) {
    if (encendido) {
      const ok = await pedirPermisoAvisos();
      setPermiso(permisoAvisos());
      if (!ok) {
        return;
      }
    }
    cambiar({ activo: encendido });
  }

  async function activarPareja(encendido: boolean) {
    if (encendido) {
      const ok = await pedirPermisoAvisos();
      setPermiso(permisoAvisos());
      if (!ok) {
        return;
      }
    }
    cambiar({ pareja: encendido });
  }

  async function mandarPrueba() {
    const frases = frasesToque(t, c.tono);
    const [titulo, cuerpo] = frases[Math.floor(Math.random() * frases.length)];
    const ok = await mostrarAviso(titulo, cuerpo);
    setPermiso(permisoAvisos());
    setPrueba(ok ? 'enviada' : 'fallo');
  }

  const proximo = proximoToque(c, new Date());
  // Aunque estén apagadas, se enseña cuántos toques serían.
  const horasHoy =
    c.ritmo === 'varias' ? horasDelDia({ ...c, activo: true }, new Date()) : [];

  return (
    <View style={{ gap: 14 }} data-testid="notificaciones-ajustes">
      <Bloque>
        <FilaInterruptor
          texto={t('Notifications on this phone')}
          detalle={
            permiso === 'denegado'
              ? t(
                  'Your phone has them blocked. Turn them on in your phone settings: Apps, Rumbo, Notifications.',
                )
              : permiso === 'sin-soporte'
                ? t('This browser cannot show notifications.')
                : undefined
          }
          encendido={c.activo}
          onChange={v => void activar(v)}
          testId="notif-activo"
        />
      </Bloque>

      <Seccion titulo={t('How often')}>
        <Bloque>
          <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 6 }}>
            {RITMOS.map(r => (
              <Chip
                key={r}
                elegido={c.ritmo === r}
                data-testid={`notif-ritmo-${r}`}
                onPress={() => cambiar({ ritmo: r })}
              >
                {nombreRitmo(t, r)}
              </Chip>
            ))}
          </View>

          {(c.ritmo === 'dia' || c.ritmo === 'dos') && (
            <Linea texto={t('At')}>
              <CampoHora
                etiqueta={t('Time of the nudge')}
                valor={c.hora}
                onChange={hora => cambiar({ hora })}
                testId="notif-hora"
              />
            </Linea>
          )}
          {c.ritmo === 'dia' && (
            <>
              <Dias
                dias={c.dias}
                onChange={dias => cambiar({ dias })}
                testId="notif-dia"
              />
              <Nota>
                <Trans>Tap a day to remove it.</Trans>
              </Nota>
            </>
          )}
          {c.ritmo === 'varias' && (
            <>
              <Linea texto={t('Every')}>
                <View
                  style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 6 }}
                >
                  {CADA_MIN.map(m => (
                    <Chip
                      key={m}
                      elegido={c.cadaMin === m}
                      data-testid={`notif-cada-${m}`}
                      onPress={() => cambiar({ cadaMin: m })}
                    >
                      {textoMinutos(t, m)}
                    </Chip>
                  ))}
                </View>
              </Linea>
              <Linea texto={t('Between')}>
                <View
                  style={{ flexDirection: 'row', alignItems: 'center', gap: 6 }}
                >
                  <CampoHora
                    etiqueta={t('From')}
                    valor={c.desde}
                    onChange={desde => cambiar({ desde })}
                    testId="notif-desde"
                  />
                  <Text style={{ color: color.fg2 }}>–</Text>
                  <CampoHora
                    etiqueta={t('Until')}
                    valor={c.hasta}
                    onChange={hasta => cambiar({ hasta })}
                    testId="notif-hasta"
                  />
                </View>
              </Linea>
              <Nota>
                {t('That is {{n}} nudges a day: {{horas}}.', {
                  n: horasHoy.length,
                  horas: horasHoy.join(', '),
                })}
              </Nota>
            </>
          )}
          {c.ritmo === 'manera' && (
            <View style={{ gap: 8 }}>
              {c.horas.map((h, i) => (
                <View
                  key={i}
                  style={{
                    gap: 8,
                    padding: 10,
                    borderRadius: radio.sm,
                    backgroundColor: color.surface2,
                  }}
                >
                  <View
                    style={{
                      flexDirection: 'row',
                      alignItems: 'center',
                      gap: 8,
                    }}
                  >
                    <CampoHora
                      etiqueta={t('Time of the nudge')}
                      valor={h.hora}
                      onChange={hora =>
                        cambiar({
                          horas: c.horas.map((x, j) =>
                            j === i ? { ...x, hora } : x,
                          ),
                        })
                      }
                      testId={`notif-propia-${i}`}
                    />
                    <View style={{ flex: 1 }} />
                    <Button
                      variant="bare"
                      aria-label={t('Remove this time')}
                      data-testid={`notif-quitar-${i}`}
                      onPress={() =>
                        cambiar({ horas: c.horas.filter((_, j) => j !== i) })
                      }
                      style={{
                        minWidth: TACTIL,
                        minHeight: TACTIL,
                        color: color.fg2,
                      }}
                    >
                      ✕
                    </Button>
                  </View>
                  <Dias
                    dias={h.dias}
                    onChange={dias =>
                      cambiar({
                        horas: c.horas.map((x, j) =>
                          j === i ? { ...x, dias } : x,
                        ),
                      })
                    }
                    testId={`notif-propia-${i}-dia`}
                  />
                </View>
              ))}
              <Boton
                variante="fantasma"
                data-testid="notif-anadir-hora"
                onPress={() =>
                  cambiar({
                    horas: [
                      ...c.horas,
                      { hora: '12:00', dias: [0, 1, 2, 3, 4, 5, 6] },
                    ],
                  })
                }
              >
                <Trans>+ Add a time</Trans>
              </Boton>
            </View>
          )}
          {c.activo && proximo && (
            <Nota>
              {t('Next nudge: {{cuando}}.', {
                cuando: proximo.toLocaleString(i18n.language, {
                  weekday: 'long',
                  hour: '2-digit',
                  minute: '2-digit',
                }),
              })}
            </Nota>
          )}
        </Bloque>
      </Seccion>

      <Seccion titulo={t('What was left pending')}>
        <Bloque>
          <FilaInterruptor
            texto={t('Let me know if I leave something')}
            encendido={c.pendiente.activo}
            onChange={activo => cambiarPendiente({ activo })}
            testId="notif-pendiente"
          />
          {c.pendiente.activo && (
            <>
              <Linea texto={t('After')}>
                <View
                  style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 6 }}
                >
                  {TRAS_PENDIENTE.map(x => (
                    <Chip
                      key={x}
                      elegido={c.pendiente.tras === x}
                      data-testid={`notif-tras-${x}`}
                      onPress={() => cambiarPendiente({ tras: x })}
                    >
                      {x === 'manana' ? t('The next day') : textoMinutos(t, x)}
                    </Chip>
                  ))}
                </View>
              </Linea>
              <Casilla
                texto={t('Transactions without a category')}
                marcada={c.pendiente.sinCategoria}
                onChange={sinCategoria => cambiarPendiente({ sinCategoria })}
                testId="notif-sin-categoria"
              />
              <Casilla
                texto={t('Categories in the red')}
                marcada={c.pendiente.enRojo}
                onChange={enRojo => cambiarPendiente({ enRojo })}
                testId="notif-en-rojo"
              />
              <Casilla
                texto={t('Transactions to review')}
                marcada={c.pendiente.porRevisar}
                onChange={porRevisar => cambiarPendiente({ porRevisar })}
                testId="notif-por-revisar"
              />
            </>
          )}
        </Bloque>
      </Seccion>

      <Seccion titulo={t("Ship's log")}>
        <Bloque>
          <FilaInterruptor
            texto={t('Let me know when it is ready')}
            detalle={t(
              'On Monday for the week and on the 1st for the month, at your first nudge of the day.',
            )}
            encendido={c.diario}
            onChange={diario => cambiar({ diario })}
            testId="notif-diario"
          />
        </Bloque>
      </Seccion>

      {compartido && (
        <Seccion titulo={t('Both aboard')}>
          <Bloque>
            <FilaInterruptor
              texto={t('Let me know when the other person adds an expense')}
              detalle={t(
                'At most one notice every five minutes. In quiet hours it arrives without sound.',
              )}
              encendido={c.pareja}
              onChange={pareja => void activarPareja(pareja)}
              testId="notif-pareja"
            />
          </Bloque>
        </Seccion>
      )}

      <Seccion titulo={t('How')}>
        <Bloque>
          <Linea texto={t("Gavi's tone")}>
            <View style={{ flexDirection: 'row', gap: 6 }}>
              <Chip
                elegido={c.tono === 'traviesa'}
                data-testid="notif-tono-traviesa"
                onPress={() => cambiar({ tono: 'traviesa' })}
              >
                <Trans>Cheeky</Trans>
              </Chip>
              <Chip
                elegido={c.tono === 'tranquila'}
                data-testid="notif-tono-tranquila"
                onPress={() => cambiar({ tono: 'tranquila' })}
              >
                <Trans>Calm</Trans>
              </Chip>
            </View>
          </Linea>
          <FilaInterruptor
            texto={t('With figures')}
            detalle={t('Amounts and category names on the lock screen.')}
            encendido={c.cifras}
            onChange={cifras => cambiar({ cifras })}
            testId="notif-cifras"
          />
          <FilaInterruptor
            texto={t('Quiet hours')}
            encendido={c.silencio.activo}
            onChange={activo => cambiarSilencio({ activo })}
            testId="notif-silencio"
          />
          {c.silencio.activo && (
            <View
              style={{ flexDirection: 'row', alignItems: 'center', gap: 6 }}
            >
              <CampoHora
                etiqueta={t('From')}
                valor={c.silencio.desde}
                onChange={desde => cambiarSilencio({ desde })}
                testId="notif-silencio-desde"
              />
              <Text style={{ color: color.fg2 }}>–</Text>
              <CampoHora
                etiqueta={t('Until')}
                valor={c.silencio.hasta}
                onChange={hasta => cambiarSilencio({ hasta })}
                testId="notif-silencio-hasta"
              />
            </View>
          )}
        </Bloque>
      </Seccion>

      <Boton
        bloque
        data-testid="notif-prueba"
        isDisabled={permiso === 'sin-soporte'}
        onPress={() => void mandarPrueba()}
      >
        <Trans>Send me a test one</Trans>
      </Boton>
      {prueba && (
        <Nota>
          {prueba === 'enviada'
            ? t('Sent. Look at the top of your screen.')
            : t('It could not be sent. Check that notifications are allowed.')}
        </Nota>
      )}
    </View>
  );
}

const LETRAS_DIAS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'] as const;

function Dias({
  dias,
  onChange,
  testId,
}: {
  dias: number[];
  onChange: (dias: number[]) => void;
  testId: string;
}) {
  const { t } = useTranslation();
  const letra: Record<(typeof LETRAS_DIAS)[number], string> = {
    Mon: t('Monday initial'),
    Tue: t('Tuesday initial'),
    Wed: t('Wednesday initial'),
    Thu: t('Thursday initial'),
    Fri: t('Friday initial'),
    Sat: t('Saturday initial'),
    Sun: t('Sunday initial'),
  };
  return (
    <View style={{ flexDirection: 'row', gap: 5 }}>
      {LETRAS_DIAS.map((d, i) => {
        const marcado = dias.includes(i);
        return (
          <Button
            key={d}
            variant="bare"
            aria-pressed={marcado}
            aria-label={letra[d]}
            data-testid={`${testId}-${i}`}
            onPress={() =>
              onChange(
                marcado
                  ? dias.filter(x => x !== i)
                  : [...dias, i].sort((a, b) => a - b),
              )
            }
            style={{
              flex: 1,
              minHeight: TACTIL - 6,
              padding: 0,
              borderRadius: 10,
              fontWeight: 700,
              fontSize: 13,
              backgroundColor: marcado ? color.accentSoft : color.surface2,
              color: marcado ? color.accent : color.fg3,
            }}
          >
            {letra[d]}
          </Button>
        );
      })}
    </View>
  );
}

function CampoHora({
  etiqueta,
  valor,
  onChange,
  testId,
}: {
  etiqueta: string;
  valor: string;
  onChange: (hora: string) => void;
  testId: string;
}) {
  return (
    <input
      type="time"
      aria-label={etiqueta}
      data-testid={testId}
      value={valor}
      onChange={e => {
        if (/^\d{2}:\d{2}$/.test(e.target.value)) {
          onChange(e.target.value);
        }
      }}
      style={{
        minHeight: 40,
        padding: '4px 10px',
        borderRadius: 10,
        border: 0,
        backgroundColor: color.surface2,
        color: color.fg,
        font: 'inherit',
        fontWeight: 700,
        fontVariantNumeric: 'tabular-nums',
      }}
    />
  );
}

function Seccion({
  titulo,
  children,
}: {
  titulo: string;
  children: ReactNode;
}) {
  return (
    <View style={{ gap: 6 }}>
      <Text
        role="heading"
        aria-level={3}
        style={{ ...texto.etiqueta, color: color.fg3, paddingLeft: 4 }}
      >
        {titulo}
      </Text>
      {children}
    </View>
  );
}

function Bloque({ children }: { children: ReactNode }) {
  return (
    <View
      style={{
        gap: 12,
        padding: 12,
        borderRadius: radio.tarjeta,
        border: `1px solid ${color.line}`,
      }}
    >
      {children}
    </View>
  );
}

function Linea({
  texto: etiqueta,
  children,
}: {
  texto: string;
  children: ReactNode;
}) {
  return (
    <View
      style={{
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'space-between',
        flexWrap: 'wrap',
        gap: 8,
      }}
    >
      <Text style={{ ...texto.cuerpo, color: color.fg }}>{etiqueta}</Text>
      {children}
    </View>
  );
}

function Nota({ children }: { children: ReactNode }) {
  return (
    <Text style={{ ...texto.secundario, color: color.fg2 }}>{children}</Text>
  );
}

/** Fila táctil a todo el ancho (interruptor o casilla), sin aspecto de botón. */
const estiloBotonFila: CSSProperties = {
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
  width: '100%',
  minHeight: TACTIL,
  padding: 0,
  border: 0,
  background: 'none',
  textAlign: 'left',
  font: 'inherit',
  color: color.fg,
  cursor: 'pointer',
  WebkitTapHighlightColor: 'transparent',
};

function FilaInterruptor({
  texto: titulo,
  detalle,
  encendido,
  onChange,
  testId,
}: {
  texto: string;
  detalle?: string;
  encendido: boolean;
  onChange: (encendido: boolean) => void;
  testId: string;
}) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={encendido}
      data-testid={testId}
      onClick={() => onChange(!encendido)}
      style={{ ...estiloBotonFila, justifyContent: 'space-between', gap: 12 }}
    >
      <View style={{ flex: 1, minWidth: 0 }}>
        <Text style={{ ...texto.fila, fontSize: 14.5, color: color.fg }}>
          {titulo}
        </Text>
        {detalle && (
          <Text style={{ ...texto.secundario, fontSize: 12, color: color.fg2 }}>
            {detalle}
          </Text>
        )}
      </View>
      <Interruptor encendido={encendido} />
    </button>
  );
}

function Casilla({
  texto: titulo,
  marcada,
  onChange,
  testId,
}: {
  texto: string;
  marcada: boolean;
  onChange: (marcada: boolean) => void;
  testId: string;
}) {
  return (
    <button
      type="button"
      role="checkbox"
      aria-checked={marcada}
      data-testid={testId}
      onClick={() => onChange(!marcada)}
      style={{ ...estiloBotonFila, justifyContent: 'flex-start', gap: 10 }}
    >
      <Marca marcada={marcada} size={22} />
      <Text style={{ ...texto.cuerpo, color: color.fg }}>{titulo}</Text>
    </button>
  );
}
